import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  COOKIE_CONSENT_OPEN_SETTINGS_EVENT,
  persistCookieConsent,
  readCookieConsent,
} from "../utils/cookieConsent";
import "./CookieConsentBanner.css";

function CookieConsentBanner() {
  const [consent, setConsent] = useState(() => readCookieConsent());
  const [feedback, setFeedback] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [analytics, setAnalytics] = useState(() =>
    Boolean(readCookieConsent()?.analytics)
  );
  const [marketing, setMarketing] = useState(() =>
    Boolean(readCookieConsent()?.marketing)
  );
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const openSettings = () => {
      const storedConsent = readCookieConsent();
      setConsent(storedConsent);
      setAnalytics(Boolean(storedConsent?.analytics));
      setMarketing(Boolean(storedConsent?.marketing));
      setModalOpen(true);
    };

    window.addEventListener(COOKIE_CONSENT_OPEN_SETTINGS_EVENT, openSettings);
    return () => {
      window.removeEventListener(
        COOKIE_CONSENT_OPEN_SETTINGS_EVENT,
        openSettings
      );
    };
  }, []);

  useEffect(() => {
    if (!modalOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    closeButtonRef.current?.focus();

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [modalOpen]);

  useEffect(() => {
    if (!feedback) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setFeedback(null);
    }, 3000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [feedback]);

  const applyConsent = (selection, feedbackState) => {
    const nextConsent = persistCookieConsent(selection);
    setConsent(nextConsent);
    setAnalytics(Boolean(nextConsent?.analytics));
    setMarketing(Boolean(nextConsent?.marketing));
    setModalOpen(false);
    setFeedback(feedbackState);
  };

  const handleAcceptAll = () => {
    applyConsent({ analytics: true, marketing: true }, "all");
  };

  const handleAcceptNecessary = () => {
    applyConsent({ analytics: false, marketing: false }, "necessary");
  };

  const handleSaveSettings = () => {
    applyConsent(
      { analytics, marketing },
      analytics || marketing ? "custom" : "necessary"
    );
  };

  const showInitialBanner = !consent && !modalOpen;

  return (
    <>
      {showInitialBanner ? (
        <section
          className="cookie-banner"
          role="region"
          aria-label="Уведомление об использовании cookie"
        >
          <div className="cookie-banner__content">
            <p className="cookie-banner__text">
              <span className="cookie-banner__icon" aria-hidden="true">
                🍪
              </span>
              Мы используем cookie для безопасности, аналитики и улучшения
              сервиса. Необязательные cookie применяются только после вашего
              выбора.{" "}
              <Link className="cookie-banner__link" to="/privacy">
                Политика конфиденциальности
              </Link>
            </p>
          </div>

          <div className="cookie-banner__actions">
            <button
              type="button"
              className="cookie-banner__button cookie-banner__button--ghost"
              onClick={() => setModalOpen(true)}
            >
              Настроить
            </button>
            <button
              type="button"
              className="cookie-banner__button cookie-banner__button--ghost"
              onClick={handleAcceptNecessary}
            >
              Только необходимые
            </button>
            <button
              type="button"
              className="cookie-banner__button cookie-banner__button--primary"
              onClick={handleAcceptAll}
            >
              Принять все
            </button>
          </div>
        </section>
      ) : null}

      {feedback ? (
        <section
          className="cookie-banner cookie-banner--accepted"
          role="status"
          aria-live="polite"
        >
          <div className="cookie-banner__content">
            <p className="cookie-banner__text">
              {feedback === "all"
                ? "Сохранены все cookie. Аналитика и маркетинговые сценарии разрешены."
                : feedback === "custom"
                  ? "Пользовательские настройки cookie сохранены."
                  : "Сохранены только необходимые cookie."}
            </p>
          </div>
          <div className="cookie-banner__actions">
            <button
              type="button"
              className="cookie-banner__button cookie-banner__button--ghost"
              onClick={() => setModalOpen(true)}
            >
              Изменить настройки
            </button>
          </div>
        </section>
      ) : null}

      {modalOpen ? (
        <div
          className="cookie-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-modal-title"
        >
          <div className="cookie-modal__content">
            <div className="cookie-modal__header">
              <h2 id="cookie-modal-title" className="cookie-modal__title">
                Настройки cookie
              </h2>
              <button
                ref={closeButtonRef}
                type="button"
                className="cookie-modal__close"
                aria-label="Закрыть настройки cookie"
                onClick={() => setModalOpen(false)}
              >
                ×
              </button>
            </div>

            <p className="cookie-modal__description">
              Необязательные cookie помогают нам анализировать стабильность
              продукта и улучшать интерфейсы. Вы можете оставить только
              необходимые cookie или разрешить отдельные категории.
            </p>

            <div className="cookie-modal__section cookie-modal__section--required">
              <h3 className="cookie-modal__section-title">Необходимые</h3>
              <p className="cookie-modal__section-desc">
                Авторизация, безопасность, сохранение темы и базовая работа
                кабинета. Эта категория всегда включена.
              </p>
            </div>

            <label className="cookie-modal__section cookie-modal__checkbox">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(event) => setAnalytics(event.target.checked)}
              />
              <span>
                <strong>Аналитические</strong>
                <span className="cookie-modal__section-desc">
                  Разрешают инструменты диагностики и продуктовой аналитики,
                  например мониторинг ошибок и usage-метрики.
                </span>
              </span>
            </label>

            <label className="cookie-modal__section cookie-modal__checkbox">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(event) => setMarketing(event.target.checked)}
              />
              <span>
                <strong>Маркетинговые</strong>
                <span className="cookie-modal__section-desc">
                  Нужны для рекламных сценариев, рекомендаций и оценки
                  маркетинговых кампаний. По умолчанию отключены.
                </span>
              </span>
            </label>

            <div className="cookie-modal__footer">
              <button
                type="button"
                className="cookie-banner__button cookie-banner__button--ghost"
                onClick={handleAcceptNecessary}
              >
                Только необходимые
              </button>
              <button
                type="button"
                className="cookie-banner__button cookie-banner__button--primary"
                onClick={handleSaveSettings}
              >
                Сохранить настройки
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default CookieConsentBanner;
